import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/shared/SEO';

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
    <div>{children}</div>
  </li>
);

const MarginMatePrivacyPage = () => {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://summarygpt.com' },
      { '@type': 'ListItem', position: 2, name: 'MarginMate', item: 'https://summarygpt.com/marginmate' },
      { '@type': 'ListItem', position: 3, name: 'Privacy Policy', item: 'https://summarygpt.com/marginmate/privacy' },
    ],
  };

  return (
    <Layout>
      <SEO
        title="MarginMate Privacy Policy"
        description="MarginMate Privacy Policy: Learn how our Chrome extension handles local device storage, encrypted cloud synchronization, and retail margin calculations for Ontario store licensees."
        ogDescription="Review the data privacy practices, local storage usage, and encrypted cloud synchronization policies for MarginMate by SummaryGPT."
        path="/marginmate/privacy"
        schema={breadcrumbSchema}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 sgpt-glow opacity-50 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/marginmate"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors mb-10 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to MarginMate
          </Link>

          <div className="flex items-center gap-5 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <Shield className="w-7 h-7 text-amber-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">MarginMate Privacy Policy</h1>
          </div>
          <p className="text-slate-500 text-sm">
            <strong className="text-slate-400">Last updated:</strong> October 24, 2026
          </p>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-5xl" />

      {/* Content */}
      <section className="relative py-16 md:py-20">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert prose-slate max-w-none">

            {/* Introduction */}
            <div className="glass-card p-8 rounded-2xl mb-6">
              <p className="text-slate-300 leading-relaxed">
                This Privacy Policy explains what information the MarginMate browser extension ("MarginMate," "we," "us," or "our") collects, how it is handled, and how it is used when you install and interact with the extension. MarginMate is an independent commercial utility developed and operated by SummaryGPT for retail store licensees and business operators.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">1. Scope and Single Purpose</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                MarginMate operates with a single purpose: to assist retail store operators and licensees in calculating landed costs, determining suggested resale pricing, monitoring wholesale cost fluctuations, and logging inventory purchase history.
              </p>
              <p className="text-slate-300 leading-relaxed">
                The browser extension component operates exclusively on wholesale commerce portals relevant to Ontario retail licensees (specifically <code>wholesale.lcbo.com</code>). It does not inspect, read, modify, or transmit content from any other websites you visit, nor does it request permissions to do so.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">2. Information We Collect</h2>
              <p className="text-slate-300 leading-relaxed mb-6">
                We adhere to strict data-minimization principles under applicable commercial privacy laws (including PIPEDA). We collect only what is necessary to deliver pricing calculations and inventory management tools:
              </p>
              <h3 className="text-lg font-semibold text-white mb-3">Store Setup &amp; Profile Data</h3>
              <ul className="space-y-3 text-slate-300 mb-6">
                <Bullet><strong className="text-white">Store Postal Code (Required):</strong> Used locally to calibrate regional landed pricing. When synchronized to our cloud systems, it is truncated to the 3-character Forward Sortation Area (FSA; e.g., <code>N9A</code>) to represent a broad district rather than an exact physical address.</Bullet>
                <Bullet><strong className="text-white">Default Target Margin % (Required):</strong> Your baseline gross or net markup percentage used to automate pricing formulas.</Bullet>
                <Bullet><strong className="text-white">Store Identifier / Name (Optional):</strong> Provided by you or detected from active checkout delivery headers to label your local interface.</Bullet>
                <Bullet><strong className="text-white">Contact Email (Optional):</strong> Voluntarily submitted during setup for profile recovery, technical support, and account synchronization.</Bullet>
              </ul>
              <h3 className="text-lg font-semibold text-white mb-3">Wholesale Catalog &amp; Landed Cost Data</h3>
              <ul className="space-y-3 text-slate-300 mb-6">
                <Bullet>Product titles, SKUs, wholesale case prices, units per case, and container volumes read directly from product pages you view to compute pre-tax landed costs and container deposit deductions.</Bullet>
              </ul>
              <h3 className="text-lg font-semibold text-white mb-3">User Adjustments &amp; Pricing History</h3>
              <ul className="space-y-3 text-slate-300 mb-6">
                <Bullet>Custom SKU-level retail pricing overrides configured and saved by you.</Bullet>
                <Bullet>Historical wholesale price records recorded across visits to notify you of supplier price changes.</Bullet>
              </ul>
              <h3 className="text-lg font-semibold text-white mb-3">Order &amp; Cart History</h3>
              <ul className="space-y-3 text-slate-300">
                <Bullet>Item descriptions, SKUs, order dates, case quantities, and total order costs recorded from your active checkout and order confirmation screens.</Bullet>
              </ul>
            </div>

            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">3. Information We Never Collect</h2>
              <ul className="space-y-3 text-slate-300">
                <Bullet><strong className="text-white">No Financial or Payment Credentials:</strong> We do not intercept, collect, read, or process payment card numbers, CVVs, bank details, or transactional payment tokens.</Bullet>
                <Bullet><strong className="text-white">No Account Passwords:</strong> We do not access or store your portal login credentials or account passwords.</Bullet>
                <Bullet><strong className="text-white">No Unrelated Activity:</strong> We do not track your browsing history, cookies, search activity, or interactions outside authorized wholesale portals.</Bullet>
              </ul>
            </div>

            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">4. How Information Is Stored and Transmitted</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                MarginMate employs a <strong className="text-white">local-first architecture</strong> supported by encrypted cloud infrastructure:
              </p>
              <ul className="space-y-3 text-slate-300">
                <Bullet><strong className="text-white">Local Device Storage (<code>chrome.storage.local</code>):</strong> Your default settings, custom pricing overrides, price change logs, draft shopping carts, and past order records are stored locally within your individual browser profile.</Bullet>
                <Bullet><strong className="text-white">Cloud Sync:</strong> To keep your profile, saved prices and order records available, the extension sends events over encrypted HTTPS to our backend. These contain: a randomly generated store identifier, your store name, the 3-character postal area (FSA), your default target margin, the prices you save, the items and costs of orders you place, and, only if you opt in, your email address and consent record.</Bullet>
              </ul>
            </div>

            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">5. Data Sharing and Third-Party Disclosure</h2>
              <ul className="space-y-3 text-slate-300">
                <Bullet><strong className="text-white">No Sale or Monetization of Personal Information:</strong> We do not sell, rent, license, or disclose your personal information, store identities, or inventory volumes to third-party data brokers, advertising networks, or commercial lead generators.</Bullet>
                <Bullet><strong className="text-white">Service Providers:</strong> Cloud data is hosted and processed using enterprise-grade serverless cloud hosting providers that maintain industry-standard security and encryption certifications.</Bullet>
                <Bullet><strong className="text-white">Legal Requirements:</strong> We will only disclose information if required by a valid court order, warrant, or binding legal process under Canadian or applicable regional law.</Bullet>
              </ul>
            </div>

            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">6. Chrome Web Store User Data Compliance</h2>
              <p className="text-slate-300 leading-relaxed">
                MarginMate’s handling of user data complies with the Chrome Web Store User Data Policy, including the Limited Use requirements. We request only the minimal permissions necessary to operate: <code>storage</code>, and access to <code>wholesale.lcbo.com</code> only.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">7. User Control, Rectification, and Data Deletion</h2>
              <ul className="space-y-3 text-slate-300">
                <Bullet><strong className="text-white">Local Data Reset:</strong> You can edit your parameters or purge all local order logs, overrides, and histories at any time using the "Clear All" function in the extension drawer or by clearing your browser's extension data.</Bullet>
                <Bullet><strong className="text-white">Cloud Data Deletion:</strong> To request the permanent deletion of your synchronized store record, optional contact email, or associated telemetry from our backend systems, contact us at the email address below with your store postal code or identifier. Requests are processed within 30 days.</Bullet>
              </ul>
            </div>

            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">8. Commercial Disclaimer &amp; Independent Utility</h2>
              <p className="text-slate-300 leading-relaxed">
                MarginMate is an independent commercial calculation utility provided by SummaryGPT. It is not affiliated with, authorized by, endorsed by, or sponsored by the Liquor Control Board of Ontario (LCBO) or wholesale.lcbo.com. All trademarked terminology is used strictly in a descriptive capacity to indicate technical compatibility. Landed cost calculations are automated approximations; business operators must verify final retail pricing against their own accounting and point-of-sale systems.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl mb-6">
              <h2 className="text-2xl font-bold text-white mb-6">9. Changes to This Policy</h2>
              <p className="text-slate-300 leading-relaxed">
                We review and update this Privacy Policy as our features evolve. If our collection, transmission, or usage practices materially change, we will update the effective date at the top of this document and provide a summary of modifications within our Chrome Web Store release documentation.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-white mb-6">10. Contact Us</h2>
              <p className="text-slate-300 leading-relaxed">
                For questions regarding this policy, data practices, or to exercise your privacy rights, contact us at:<br />
                <a href="mailto:developer@summarygpt.com" className="text-amber-400 hover:text-amber-300 transition-colors">developer@summarygpt.com</a>
              </p>
            </div>

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MarginMatePrivacyPage;
